'use client';
// Cards of the v9 design: brand (.rvg), partner offer (.of), ready moment (.mo), occasion tile (.oc-tile).
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { getLocalizedPath } from '@/lib/getLocalizedPath';
import type { Brand } from '@/lib/v9/brands';
import type { Occasion, VenuePackage } from '@/lib/v9/data';
import type { DesignOccasion } from '@/lib/v9/designOccasions';
import { occasionSlug } from '@/lib/v9/occasions';
import { Sparkle, Star } from './icons';
import { Price, cx, useLang } from './ui';
import { cityOf, districtOf, venueAlt } from '@/lib/seo/venue';
import { imageProps } from '@/lib/seo/media';
import Lightbox, { type Zoom } from './Lightbox';
import { useState } from 'react';

export { bookPath } from '@/lib/v9/paths';

export function DealPills({ brand, className }: { brand: Pick<Brand, 'discountPct' | 'cashbackPct'>; className?: string }) {
  const t = useTranslations('v9.common');
  if (!brand.discountPct && !brand.cashbackPct) return null;
  return (
    <span className={cx('flex flex-wrap gap-1', className)}>
      {!!brand.discountPct && <em className="inline-flex min-h-[24px] items-center rounded-full bg-sun px-2 text-[11.5px] font-extrabold not-italic leading-none text-deep">{t('discount', { pct: Math.round(brand.discountPct) })}</em>}
      {!!brand.cashbackPct && <em className="inline-flex min-h-[24px] items-center rounded-full bg-ok-soft px-2 text-[11.5px] font-extrabold not-italic leading-none text-[#1E6B45]">{t('cashback', { pct: Math.round(brand.cashbackPct) })}</em>}
    </span>
  );
}

/** Alt text of a brand's photo (SEO item 8): "{name} في حي {district}، {city}"; the district only for a single branch. */
export function brandAlt(brand: Brand, lang: 'ar' | 'en') {
  const first = brand.branches[0];
  return venueAlt(brand.name[lang], brand.branchCount === 1 ? districtOf(first, lang) : '', cityOf(first, lang), lang);
}

/** Tall photo card of a brand: rating, tags, deals, logo, name, district and per-person price. */
export function BrandCard({ brand, href, className, priority }: { brand: Brand; href: string; className?: string; priority?: boolean }) {
  const t = useTranslations('v9.common');
  const { lang } = useLang();
  const sub = brand.distanceKm != null
    ? t('km', { n: brand.distanceKm < 10 ? brand.distanceKm.toFixed(1) : Math.round(brand.distanceKm) })
    : brand.area ? brand.area[lang] : brand.branchCount > 1 ? t('branches', { n: brand.branchCount }) : '';
  return (
    <Link
      href={href}
      className={cx(
        'group relative block aspect-[3/4] overflow-hidden rounded-[22px] bg-panel text-white shadow-[0_18px_34px_-26px_rgba(57,22,77,.6)] transition-[transform,box-shadow] duration-300 ease-mila hover:-translate-y-[3px] hover:shadow-[0_26px_40px_-24px_rgba(57,22,77,.7)]',
        "before:pointer-events-none before:absolute before:inset-0 before:z-[1] before:bg-[linear-gradient(to_top,rgba(30,10,46,.97)_0%,rgba(30,10,46,.86)_26%,rgba(30,10,46,.5)_50%,rgba(30,10,46,0)_74%)] before:content-['']",
        "after:pointer-events-none after:absolute after:inset-1.5 after:rounded-[18px] after:shadow-[inset_0_0_0_1px_rgba(240,213,140,.75)] after:content-['']",
        className,
      )}
    >
      {brand.photo && <Image {...imageProps(brand.photo)} alt={brandAlt(brand, lang)} fill priority={priority} sizes="(max-width: 640px) 62vw, (max-width: 1024px) 33vw, 300px" className="object-cover transition-transform duration-700 ease-mila group-hover:scale-[1.04]" />}
      {brand.rating != null && (
        <span className="absolute start-3 top-3 z-[2] inline-flex min-h-[26px] items-center gap-1 rounded-full bg-white/[.94] px-[9px] text-[12.5px] font-extrabold text-deep">
          <Star className="h-3 w-3 text-[#D9A83F]" /><span dir="ltr">{brand.rating.toFixed(1)}</span>
        </span>
      )}
      <span className="absolute end-3 top-3 z-[2] flex flex-col items-end gap-1.5">
        {brand.featured && <span className="inline-flex min-h-[26px] items-center gap-1 rounded-full bg-white/[.94] px-[9px] text-[11.5px] font-extrabold text-deep"><Sparkle className="h-[11px] w-[11px] text-gold" />{t('milaPicks')}</span>}
        {!brand.featured && brand.trending && <span className="inline-flex min-h-[26px] items-center rounded-full bg-[rgba(45,16,64,.84)] px-[9px] text-[11.5px] font-extrabold text-sun">{t('trending')}</span>}
        {!!brand.discountPct && <em className="inline-flex min-h-[26px] items-center rounded-full bg-sun px-[9px] text-[11.5px] font-extrabold not-italic text-deep shadow-[0_8px_16px_-10px_rgba(30,10,46,.8)]">{t('discount', { pct: Math.round(brand.discountPct) })}</em>}
        {!!brand.cashbackPct && <em className="inline-flex min-h-[26px] items-center rounded-full bg-white/[.94] px-[9px] text-[11.5px] font-extrabold not-italic text-[#1E6B45] shadow-[0_8px_16px_-10px_rgba(30,10,46,.8)]">{t('cashback', { pct: Math.round(brand.cashbackPct) })}</em>}
      </span>
      <span className="absolute inset-x-3 bottom-3 z-[2] flex min-w-0 flex-col gap-0.5 [text-shadow:0_1px_2px_rgba(20,4,32,.85),0_4px_14px_rgba(20,4,32,.6)]">
        {brand.logo && (
          <span className="mb-1.5 block h-[38px] w-[38px] overflow-hidden rounded-full bg-white shadow-[0_0_0_1px_rgba(217,180,95,.9)]">
            <Image {...imageProps(brand.logo)} alt="" width={38} height={38} sizes="38px" className="h-full w-full object-contain" />
          </span>
        )}
        <span className="truncate text-[17px] font-extrabold leading-[1.4]">{brand.name[lang]}</span>
        {sub && <span className="truncate font-body text-[13px] font-semibold leading-[1.5] text-white/[.96]">{sub}</span>}
        {brand.price != null && (
          <span className="mt-0.5 inline-flex flex-wrap items-baseline gap-1 text-[16px] font-extrabold">
            <Price amount={brand.price} />
            {brand.originalPrice != null && <s className="text-[12.5px] font-bold text-white/[.82] decoration-[1.5px]"><Price amount={brand.originalPrice} /></s>}
            <small className="font-body text-[12px] font-semibold text-white/[.94]">/ {t('perPerson', { unit: brand.unit })}</small>
          </span>
        )}
      </span>
    </Link>
  );
}

/** Row card of a brand with a deal (home "Partner offers"). */
export function OfferCard({ brand, href }: { brand: Brand; href: string }) {
  const t = useTranslations('v9.common');
  const th = useTranslations('v9.home');
  const { lang } = useLang();
  return (
    <Link href={href} className="flex items-center gap-3 rounded-[20px] bg-white p-3 text-deep shadow-[inset_0_0_0_1px_rgba(57,22,77,.1)] transition-shadow hover:shadow-[inset_0_0_0_1px_rgba(85,39,109,.35)]">
      <span className="grid h-[54px] w-[54px] flex-none place-items-center overflow-hidden rounded-full bg-white font-extrabold text-plum shadow-[0_0_0_1px_rgba(217,180,95,.9)]">
        {brand.logo ? <Image {...imageProps(brand.logo)} alt="" width={54} height={54} sizes="54px" className="h-full w-full object-contain" /> : brand.name[lang].charAt(0)}
      </span>
      <span className="grid min-w-0 flex-1 gap-1">
        <span className="truncate text-[15px] font-extrabold leading-[1.4]">{brand.name[lang]}</span>
        <DealPills brand={brand} />
        <small className="font-body text-[12px] text-deep/[.62]">
          {brand.cashbackPct && !brand.discountPct ? th('cashbackNote', { brand: brand.name[lang] }) : (
            brand.price != null && (
              <span className="inline-flex flex-wrap items-baseline gap-1">
                {brand.originalPrice != null && <s className="opacity-55"><Price amount={brand.originalPrice} /></s>}
                <Price amount={brand.price} /> / {t('perPerson', { unit: brand.unit })}
              </span>
            )
          )}
        </small>
      </span>
      <span className="inline-flex min-h-9 flex-none items-center rounded-full bg-panel px-3 text-[12.5px] font-extrabold text-plum">{t('book')}</span>
    </Link>
  );
}

/**
 * Ready moment: a venue bundle at one price. With `addons` (the bundle's add-ons: photo + name) the card follows the
 * approved design: a plum band with the add-ons' round photos and the count, then the name, what's inside, the price.
 * Without them (still loading, or a bundle with no add-on photos) it is the photo card as before.
 */
export function MomentCard({ pkg, href, price: fromPrice, addons: bundleAddons, variant = 'band', branchPhoto = null, selected = false, onRemove, scroll, onClick }: { pkg: VenuePackage; href: string; price?: number | null; addons?: { image: string | null; name_ar: string; name_en: string }[]; variant?: 'band' | 'photo'; /** The branch's first photo: the restaurant photo of the card. */ branchPhoto?: string | null; /** The bundle the booking draft started from (design .bpb.is-on): «Selected», Remove + Continue. */ selected?: boolean; onRemove?: () => void; scroll?: boolean; onClick?: () => void }) {
  const t = useTranslations('v9.common');
  const th = useTranslations('v9.home');
  const { pick } = useLang();
  const price = fromPrice ?? pkg.pricing?.price ?? null;
  const photos = (bundleAddons || []).map((a) => a.image).filter((s): s is string => !!s).slice(0, 3);
  const inside = (bundleAddons || []).map((a) => pick(a.name_ar, a.name_en)).filter(Boolean).join(' · ');
  // Home: the restaurant photo stays, with the add-ons as round photos on it and their names under the title.
  if (variant === 'photo') return <MomentPhotoCard pkg={pkg} href={href} price={price} photos={photos} inside={inside} branchPhoto={branchPhoto} scroll={scroll} onClick={onClick} />;
  // The add-ons (photos, price) arrive a moment after the page: until then the band shows placeholders, never the
  // old photo card, so the card does not change shape in front of the guest.
  const loading = bundleAddons === undefined;
  if (photos.length || loading) {
    // Design .bpb__c: plum band with the add-ons' round photos and the count, name, what's inside, price and the button.
    return (
      <div className={cx('flex flex-col overflow-hidden rounded-[20px] bg-white text-start text-deep transition-[box-shadow] duration-300', selected ? 'shadow-[0_0_0_2px_rgb(var(--c-plum)),0_18px_34px_-24px_rgba(57,22,77,.55)]' : 'shadow-[0_0_0_1px_rgba(57,22,77,.08),0_18px_34px_-28px_rgba(57,22,77,.45)]')}>
        <Link href={href} scroll={scroll} onClick={onClick} className="relative flex items-center justify-between gap-2 p-3.5 bg-[radial-gradient(120%_100%_at_100%_0%,#8257AE,rgba(130,87,174,0)_60%),linear-gradient(160deg,#6A3A8C,#4A2062_75%)]">
          <span className="flex flex-none items-center">
            {loading
              ? [0, 1, 2].slice(0, Math.min(3, pkg.components_count || 3)).map((i) => <span key={i} className={cx('h-12 w-12 flex-none rounded-full bg-white/25 shadow-[0_0_0_2px_rgba(240,213,140,.6)]', i > 0 && '-me-2.5')} />)
              : photos.map((src, i) => <img key={i} src={src} alt="" loading="lazy" className={cx('h-12 w-12 flex-none rounded-full bg-white object-cover shadow-[0_0_0_2px_rgba(240,213,140,.95)]', i < photos.length - 1 && '-me-2.5')} />)}
          </span>
          <i className="flex-none whitespace-nowrap rounded-full bg-sun px-2.5 py-[3px] text-[11.5px] font-extrabold not-italic text-deep">{selected ? t('selected') : th('addons', { n: pkg.components_count || photos.length })}</i>
        </Link>
        <span className="flex flex-1 flex-col gap-1 px-3.5 pb-3.5 pt-3">
          <Link href={href} scroll={scroll} onClick={onClick} className="text-[15.5px] font-extrabold leading-[1.4]">{pick(pkg.name_ar, pkg.name_en)}</Link>
          {/* Two lines kept while loading, so the card does not grow when the add-ons arrive. */}
          {inside ? <span className="line-clamp-2 min-h-[36px] font-body text-[12px] leading-[1.5] text-deep/[.62]">{inside}</span>
            : loading ? <span className="h-[36px] w-3/4 rounded bg-panel" /> : null}
          <span className="mt-auto flex items-center gap-2 pt-1.5">
            {price != null ? <span className="me-auto inline-flex items-baseline gap-1 text-[18px] font-extrabold text-plum"><small className="font-body text-[12px] text-deep/[.62]">{t('from')}</small><Price amount={price} /></span>
              : price === undefined ? <span className="me-auto h-[22px] w-20 rounded bg-panel" /> : null}
            {selected && onRemove && <button type="button" onClick={onRemove} className="inline-flex min-h-[38px] items-center rounded-full bg-white px-3 text-[12.5px] font-extrabold text-plum shadow-[inset_0_0_0_1.5px_rgba(85,39,109,.4)]">{t('removeBundle')}</button>}
            <Link href={href} scroll={scroll} onClick={onClick} className="inline-flex min-h-[38px] items-center rounded-full bg-plum px-4 text-[13px] font-extrabold text-white">{selected ? t('continueBooking') : t('bookIt')}</Link>
          </span>
        </span>
      </div>
    );
  }
  return <MomentPhotoCard pkg={pkg} href={href} price={price} branchPhoto={branchPhoto} scroll={scroll} onClick={onClick} />;
}

/** The photo card (the branch's photo, else the seating's, else the package's); with `photos`/`inside` the add-ons sit on it and under the title. */
function MomentPhotoCard({ pkg, href, price, photos = [], inside = '', branchPhoto = null, scroll, onClick }: { pkg: VenuePackage; href: string; price: number | null; photos?: string[]; inside?: string; branchPhoto?: string | null; scroll?: boolean; onClick?: () => void }) {
  const t = useTranslations('v9.common');
  const th = useTranslations('v9.home');
  const { pick } = useLang();
  // The restaurant photo is the branch's (photos are uploaded on the physical space, not the seating); the seating's cover and the package image are only fallbacks.
  const img = branchPhoto || pkg.venue?.cover_image || pkg.main_images?.[0] || null;
  const venueName = pick(pkg.venue?.physical_space?.name_ar, pkg.venue?.physical_space?.name_en);
  const sub = [venueName, inside].filter(Boolean).join(' · ') || pick(pkg.description_ar, pkg.description_en);
  // The photo full size (requirements §14), without leaving the card's link.
  const [zoom, setZoom] = useState<Zoom | null>(null);
  return (
    <>
    {zoom && <Lightbox zoom={zoom} onClose={() => setZoom(null)} closeLabel={t('close')} />}
    <Link href={href} scroll={scroll} onClick={onClick} className="flex flex-col overflow-hidden rounded-[22px] bg-white text-start text-deep shadow-[0_1px_0_rgba(57,22,77,.06),0_22px_40px_-30px_rgba(57,22,77,.4)] transition-transform duration-300 ease-mila active:scale-[.985]">
      <span className="relative block w-full overflow-hidden bg-panel pb-[60%]">
        {img && <Image {...imageProps(img)} alt={venueName || pick(pkg.name_ar, pkg.name_en)} fill sizes="(max-width: 640px) 82vw, 380px" className="object-cover" />}
        {img && (
          <button type="button" aria-label={pick(pkg.name_ar, pkg.name_en)} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setZoom({ src: img, name: pick(pkg.name_ar, pkg.name_en) }); }}
            className="absolute start-2.5 top-2.5 grid h-8 w-8 place-items-center rounded-full bg-white/[.92] text-plum shadow-[0_8px_16px_-10px_rgba(30,10,46,.8)]">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5M11 8v6M8 11h6" /></svg>
          </button>
        )}
        <span className="absolute end-2.5 top-2.5 rounded-full bg-[rgba(45,16,64,.84)] px-2.5 py-[3px] text-[11.5px] font-extrabold text-sun">{th('allIn')}</span>
        {!!pkg.components_count && <span className="absolute bottom-3 end-2.5 rounded-full bg-white/[.94] px-[9px] py-[3px] text-[11.5px] font-extrabold text-plum shadow-[0_0_0_1px_rgba(217,180,95,.8)]">{th('addons', { n: pkg.components_count })}</span>}
        {photos.length > 0 && (
          <span className="absolute bottom-2.5 start-2.5 flex items-center">
            {photos.map((src, i) => <img key={i} src={src} alt="" loading="lazy" className={cx('h-[52px] w-[52px] flex-none rounded-full object-cover shadow-[0_0_0_2.5px_#fff,0_8px_16px_-8px_rgba(30,10,46,.8)]', i > 0 && '-ms-3')} />)}
          </span>
        )}
      </span>
      <span className="flex flex-1 flex-col gap-1 px-3.5 pb-3.5 pt-3">
        <span className="text-[16px] font-extrabold leading-[1.5]">{pick(pkg.name_ar, pkg.name_en)}</span>
        <span className="line-clamp-2 font-body text-[12.5px] leading-[1.5] text-deep/[.62]">{sub}</span>
        <span className="mt-auto flex items-center justify-between gap-2 pt-1">
          {price != null && <span className="inline-flex items-baseline gap-1 text-[18px] font-extrabold text-plum"><small className="font-body text-[12px] text-deep/[.62]">{t('from')}</small><Price amount={price} /></span>}
          <span className="inline-flex min-h-[38px] items-center rounded-full bg-plum px-4 text-[13px] font-extrabold text-white">{t('bookIt')}</span>
        </span>
      </span>
    </Link>
    </>
  );
}

const OCC_ART: Record<string, string> = {
  birthday: '/v9/occ-birthday.webp',
  graduation: '/v9/occ-graduation.webp',
  anniversary: '/v9/occ-anniversary.webp',
  'special-achievement': '/v9/occ-achievement.webp',
  achievement: '/v9/occ-achievement.webp',
};

export function occasionImage(o: Occasion) {
  return OCC_ART[occasionSlug(o)] || o.cover_image || null;
}

export function OccasionTile({ occasion, image, href, label, plain }: { occasion?: Occasion; image?: string; href: string; label: string; plain?: boolean }) {
  const img = image ?? (occasion ? occasionImage(occasion) : null);
  return (
    <Link href={href} className={cx(
      'relative block aspect-[16/10] overflow-hidden rounded-[18px] text-white',
      plain ? 'bg-plum' : "bg-panel after:absolute after:inset-0 after:bg-[linear-gradient(to_top,rgba(30,10,46,.85),rgba(30,10,46,.1)_60%)] after:content-['']",
    )}>
      {img && <img src={img} alt="" loading="lazy" className="block h-full w-full object-cover" />}
      <b className="absolute bottom-2.5 start-3 z-[1] text-[16px] font-extrabold [text-shadow:0_1px_2px_rgba(20,4,32,.6)]">{label}</b>
    </Link>
  );
}

export function OccasionsGrid({ occasions, locale, regularLabel }: { occasions: DesignOccasion[]; locale: string; regularLabel: string }) {
  const { pick } = useLang();
  const L = (p: string) => getLocalizedPath(locale, p);
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
      {occasions.map((o) => (
        <OccasionTile key={o.key} image={o.image} href={L(o.slug ? `/occasions/${o.slug}` : '/reserve')} label={pick(o.name_ar, o.name_en)} />
      ))}
      <OccasionTile image="/v9/stack-base.webp" href={L('/reserve')} label={regularLabel} />
    </div>
  );
}
